import { z } from 'zod';
import { objectId } from './schemas.js';
import { LEARNING_SOURCES, LEARNING_SOURCE_LABELS } from './learning.js';
/**
 * Insights: what the outside world is saying, read through Composio and
 * summarised by the assistant. Three parts share one page and one switch:
 *
 * - **Watch** — mentions of the company and its competitors on social media and
 *   in the news, each tagged positive, neutral or negative. A negative mention
 *   of the company can be posted to a channel as soon as it is found.
 * - **News** — a morning brief of the industry's news on the team's topics.
 * - **Trends** — a weekly read of rising and falling search interest in the
 *   workspace's keywords, with a line on each from the assistant.
 */
export const INSIGHT_KINDS = ['watch', 'news', 'trends'];
/* ------------------------------- sources -------------------------------- */
export const WATCH_SOURCES = ['news', ...LEARNING_SOURCES];
export const WATCH_SOURCE_LABELS = {
    news: 'News',
    ...LEARNING_SOURCE_LABELS,
};
/* ------------------------------- limits --------------------------------- */
export const WATCH_MAX_BRAND_TERMS = 5;
export const WATCH_MAX_COMPETITORS = 8;
export const NEWS_MAX_TOPICS = 8;
/** Google Trends compares at most five terms in one series. */
export const TRENDS_MAX_KEYWORDS = 5;
export const INSIGHT_TERM_MAX_LENGTH = 60;
/** How often brand watch looks for new mentions. */
export const WATCH_INTERVAL_HOURS = 6;
export const INSIGHTS_DEFAULT_TIME = '08:00';
export const INSIGHTS_FEED_PAGE_SIZE = 20;
/* -------------------------------- watch --------------------------------- */
export const MENTION_KINDS = ['brand', 'competitor'];
export const MENTION_SENTIMENTS = ['positive', 'neutral', 'negative'];
/* ------------------------------- trends --------------------------------- */
export const TREND_DIRECTIONS = ['rising', 'falling', 'steady'];
export const DEFAULT_INSIGHTS_SETTINGS = {
    time: INSIGHTS_DEFAULT_TIME,
    watch: { brandTerms: [], competitors: [], sources: ['news', 'hackernews', 'reddit'], alertChannelId: null },
    news: { topics: [], region: '', language: 'en', channelId: null },
    trends: { keywords: [], channelId: null },
};
/* ------------------------------- schemas -------------------------------- */
const term = z.string().trim().min(2).max(INSIGHT_TERM_MAX_LENGTH);
/** Case-insensitive de-duplication, keeping the first spelling. */
const unique = (terms) => terms.filter((value, index) => terms.findIndex((other) => other.toLowerCase() === value.toLowerCase()) === index);
const terms = (max) => z.array(term).max(max).transform(unique);
const channel = objectId.nullable();
export const updateInsightsSettingsSchema = z
    .object({
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm'),
    watch: z
        .object({
        brandTerms: terms(WATCH_MAX_BRAND_TERMS),
        competitors: terms(WATCH_MAX_COMPETITORS),
        sources: z
            .array(z.enum(WATCH_SOURCES))
            .min(1, 'Pick at least one source')
            .transform((sources) => [...new Set(sources)]),
        alertChannelId: channel,
    })
        .partial(),
    news: z
        .object({
        topics: terms(NEWS_MAX_TOPICS),
        region: z.string().trim().toLowerCase().regex(/^([a-z]{2})?$/, 'Use a two-letter country code'),
        language: z.string().trim().toLowerCase().regex(/^[a-z]{2}(-[a-z]{2})?$/, 'Use a language code like en'),
        channelId: channel,
    })
        .partial(),
    trends: z.object({ keywords: terms(TRENDS_MAX_KEYWORDS), channelId: channel }).partial(),
})
    .partial()
    .refine((body) => Object.keys(body).length > 0, 'At least one field is required');
export const insightsRunSchema = z.object({ kind: z.enum(INSIGHT_KINDS) });
export const mentionFeedQuerySchema = z.object({
    cursor: objectId.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(INSIGHTS_FEED_PAGE_SIZE),
    sentiment: z.enum(MENTION_SENTIMENTS).optional(),
    kind: z.enum(MENTION_KINDS).optional(),
});
export const newsFeedQuerySchema = z.object({
    cursor: objectId.optional(),
    limit: z.coerce.number().int().min(1).max(20).default(7),
});
